import {
  forwardRef,
  ReactNode,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import { Flex, FlexProps, Upload, message } from "antd";
import { UploadFile, UploadProps } from "antd/es/upload/interface";
import { useTranslate, useUploadFiles } from "#src/hooks";
import { InputHelperText } from "../InputHelperText";
import styled from "styled-components";
import { useAntTheme } from "hooks/useAntTheme";
import { CreateUploadableAssetRequest } from "pages/media/types/";
import { formatAcceptedFormats, isAcceptedFormat } from "helpers/uploader/";
import {
  RecursivePartial,
  StagedAsset,
  UploadFileCustom,
  UploadFilesCustomUrl,
} from "./types";
import { SortableSections } from "../sortable/SortableSections";
import { arrayMove } from "@dnd-kit/sortable";
import { DragEndEvent } from "@dnd-kit/core";
import { UploadInfo } from "./UploadInfo";
import { UploadedItem } from "./UploadedItem";

const { Dragger } = Upload;

export type UploadFilesProps = {
  action?: string;
  maxFileSize?: number; // Size in MB
  initialFile?: UploadFile;
  children?: ReactNode;
  onAbort?: () => void;
  onUploadComplete?: (files: UploadFile[], results: unknown[]) => void;
  itemRequestProperties:
    | RecursivePartial<CreateUploadableAssetRequest>
    | ((
        file: UploadFileCustom | undefined
      ) => RecursivePartial<CreateUploadableAssetRequest>);
  onFilesAdded?: (fileList: UploadFile[]) => void;
  acceptedMimeTypes: string;
  renderPreview?: (
    file: UploadFile,
    stagedItems?: StagedAsset,
    setStagedItems?: React.Dispatch<React.SetStateAction<StagedAsset>>,
    assets?: unknown[]
  ) => JSX.Element;
  required?: boolean;
  stagedItems?: StagedAsset;
  setStagedItems?: React.Dispatch<React.SetStateAction<StagedAsset>>;
  defaultItems?: unknown[];
  uploadInstructionsProps?: FlexProps;
  title?: ReactNode;
  hint?: ReactNode;
  style?: React.CSSProperties;
  multiple?: boolean;
  showErrorNotification?: boolean;
  sortable?: boolean;
  onSort?: (fileList: UploadFile[]) => void;
} & UploadProps;

export interface UploadFilesRef {
  upload: (
    customUrl?: string | ((fileName: string) => string)
  ) => Promise<number | undefined>;
  reset: () => void;
  updateProgress: (uid: string, percent: number) => void;
}

const UploadFiles = forwardRef<UploadFilesRef | undefined, UploadFilesProps>(
  function UploadFiles(
    {
      action,
      onAbort,
      maxFileSize, // in megabytes
      children,
      onUploadComplete,
      itemRequestProperties: assetRequestProperties,
      onFilesAdded,
      acceptedMimeTypes,
      renderPreview,
      required = false,
      stagedItems: stagedAssets,
      setStagedItems: setStagedAssets,
      defaultItems: assets,
      uploadInstructionsProps = {
        vertical: true,
        justify: "center",
        style: {
          minHeight: 100,
        },
      },
      multiple = true, //if multiple files choosing is enabled
      style,
      sortable = false,
      onSort,
      ...props
    },
    ref
  ) {
    const t = useTranslate();
    const {
      getS3LinksToUploadFiles,
      uploadFileToS3,
      fileList,
      setFileList,
      aggregatedDocumentsBase,
    } = useUploadFiles({ assetRequestProperties, stagedAssets });

    const validateUploadArgs = (customUrl?: UploadFilesCustomUrl) => {
      if (!action && !customUrl) {
        throw new TypeError("Either action or customUrl must be provided.");
      }
      if (fileList.length === 0 && required) {
        message.warning(t("src.components.UploadFiles.pleaseSelect"));
      }
    };

    const validateFileInfo = (file: UploadFile) => {
      const notAccepted = !isAcceptedFormat(file, acceptedMimeTypes);
      const fileTooLarge = !!(
        maxFileSize &&
        file.size &&
        file.size > maxFileSize * 1024 * 1024
      );
      return { notAccepted, fileTooLarge };
    };

    // Extract purpose from asset request properties (not all request types have purpose)
    const getPurposeFromProps = (file: UploadFile) => {
      const props =
        typeof assetRequestProperties === "function"
          ? assetRequestProperties(file)
          : assetRequestProperties;
      return (props as { purpose?: string }).purpose;
    };

    const handleUpload = async (customUrl?: UploadFilesCustomUrl) => {
      validateUploadArgs(customUrl);

      const s3Links = await getS3LinksToUploadFiles({
        fileListToUpload: fileList,
        customUrl,
      });
      // Create the aggregate entity before any file is sent to S3.
      if (aggregatedDocumentsBase) {
        await getS3LinksToUploadFiles({
          fileListToUpload: [aggregatedDocumentsBase],
          customUrl,
        });
      }
      const uploadPromises = uploadFileToS3(fileList, s3Links);

      return Promise.all(uploadPromises).then((uploads) => {
        onUploadComplete?.(fileList, s3Links);

        return uploads.flat().filter((asset) => !!asset).length;
      });
    };

    useImperativeHandle(ref, () => ({
      upload: handleUpload,
      reset: () => {
        setFileList([]);
      },
      updateProgress: (uid, percent) => {
        setFileList((prev) =>
          prev.map((item) => (item.uid === uid ? { ...item, percent } : item))
        );
      },
    }));

    const processingBatch = useRef(false);
    const batchFiles = useRef<UploadFile[]>([]);

    const fileCardWidth = () => {
      if (fileList.length > 20) return 100;
      if (fileList.length > 10) return 150;
      return 200;
    };

    const cardWidth = fileCardWidth();

    const handleBeforeUpload = (file: UploadFile) => {
      const { notAccepted, fileTooLarge } = validateFileInfo(file);
      if (notAccepted || fileTooLarge) {
        return;
      }

      // Start tracking if this is the first file
      if (!processingBatch.current) {
        processingBatch.current = true;
        batchFiles.current = [];
      }

      // Add file to current batch
      batchFiles.current.push(file);

      // Use microtask to check if more files are coming
      queueMicrotask(() => {
        // If we're still processing and this is the last file added
        if (processingBatch.current && batchFiles.current.at(-1) === file) {
          if (onFilesAdded) {
            onFilesAdded([...batchFiles.current]);
          }

          // Reset for next batch
          processingBatch.current = false;
          batchFiles.current = [];
        }
      });

      // Extract purpose to include in staged asset
      // This ensures validation passes even when BrandKitAssetPreview doesn't render
      const purpose = getPurposeFromProps(file);

      // This populates the asset with the fields used in the asset request (purpose, etc.)
      setStagedAssets?.((prev) => {
        const newAsset = {
          uid: file.uid,
          ...(purpose && { purpose }),
        };
        if (prev) {
          return [...prev, newAsset];
        }
        return [newAsset];
      });

      return false; // Prevent auto upload
    };

    const renderPreviewContent = (originNode: ReactNode, file: UploadFile) => {
      return renderPreview
        ? renderPreview(file, stagedAssets, setStagedAssets, assets)
        : originNode;
    };

    const itemRender = (
      originNode: ReactNode,
      file: UploadFile,
      _fileList: UploadFile[],
      { remove }: { remove: () => void }
    ) => {
      return (
        <UploadedItem
          sortable={sortable}
          cardWidth={cardWidth}
          file={file}
          onAbort={() => {
            onAbort?.();
          }}
          remove={remove}
          previewComponent={renderPreviewContent(originNode, file)}
        />
      );
    };

    const draggerProps: UploadProps = {
      name: "file",
      fileList,
      multiple,
      accept: acceptedMimeTypes,
      onChange: (info) => {
        const { notAccepted, fileTooLarge } = validateFileInfo(info.file);
        if (notAccepted) {
          message.error(
            t("src.components.UploadFiles.notAccepted", {
              formats: formatAcceptedFormats(acceptedMimeTypes),
            })
          );
          return;
        }
        if (fileTooLarge) {
          message.error(
            t("src.components.UploadFiles.fileTooLarge", { size: maxFileSize })
          );
          return;
        }

        // Update file list
        setFileList([...info.fileList]);

        // Antd will replace a file if maxCount = 1
        // but will not do anything if maxCount > 1
        if (props.maxCount === 1) {
          setStagedAssets?.((prev) => {
            const isRemoving = info.file.status === "removed";

            if (isRemoving) {
              // Remove the previous item if it's there
              return prev?.filter((asset) => asset.uid !== info.file.uid) || [];
            } else {
              const purpose = getPurposeFromProps(info.file);
              return [
                ...(prev || []),
                {
                  uid: info.file.uid,
                  ...(purpose && { purpose }),
                },
              ];
            }
          });
        }
      },
      itemRender,
      beforeUpload: handleBeforeUpload,
      onRemove: (file) => {
        setStagedAssets?.((prev) =>
          prev.filter((asset) => asset.uid !== file.uid)
        );
        setFileList((prevList) =>
          prevList.filter((item) => item.uid !== file.uid)
        );
      },
      ...props,
    };

    const { theme } = useAntTheme();

    const uploadBorderColor = useMemo(() => {
      if (style?.borderColor) {
        return style.borderColor;
      }
      return theme.colorPrimary;
    }, [theme, style?.borderColor]);

    const renderInnerContent = useCallback(() => {
      if (props.title) {
        return props.title;
      }
      if (children) {
        return children;
      }
      return (
        <UploadInfo
          acceptedMimeTypes={acceptedMimeTypes}
          maxFileSize={maxFileSize}
          {...uploadInstructionsProps}
        />
      );
    }, [
      children,
      props.title,
      uploadInstructionsProps,
      maxFileSize,
      acceptedMimeTypes,
    ]);

    const handleDragEnd = (event: DragEndEvent) => {
      const { active, over } = event;

      const oldIndex = fileList.findIndex((file) => file?.name === active?.id);
      const newIndex = fileList.findIndex((file) => file?.name === over?.id);

      if (active?.id !== over?.id) {
        const newSortedFiles = arrayMove(fileList, oldIndex, newIndex);
        setFileList(newSortedFiles);
        onSort?.(newSortedFiles);
      }
    };

    return (
      <SortableSections
        sortedItems={fileList.map((file) => file.name)}
        handleDragEnd={handleDragEnd}
      >
        <Flex style={{ width: "100%" }} vertical gap={10}>
          <StyledDragger
            hasPreview={!!renderPreview}
            cardWidth={cardWidth}
            {...draggerProps}
            borderColor={uploadBorderColor}
            backgroundColor={style?.backgroundColor}
          >
            {renderInnerContent()}
          </StyledDragger>
          {props.hint ?? (
            <InputHelperText>
              {t("src.components.UploadFiles.uploadWillStart")}
            </InputHelperText>
          )}
        </Flex>
      </SortableSections>
    );
  }
);

export default UploadFiles;

function DraggerWrapper({
  hasPreview,
  ...props
}: {
  readonly hasPreview?: boolean;
} & UploadProps) {
  return <Dragger {...props} />;
}

export const StyledDragger = styled(DraggerWrapper)<{
  borderColor: string;
  cardWidth?: number;
  backgroundColor?: string;
}>`
  width: 100%;
  & .ant-upload-list {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    margin-top: 16px;
    width: 100%;
    background-color: ${({ backgroundColor }) => backgroundColor};
  }

  ${({ hasPreview, cardWidth }) => {
    const widthValue = cardWidth ? `${cardWidth}px` : "100%";
    return hasPreview
      ? ""
      : `& .ant-upload-list-item-container {
    width: ${widthValue};
  }`;
  }}

  .ant-upload {
    border: none;
    height: fit-content;
    transition: height 0.3s ease-in-out;
  }
  .ant-upload-btn {
    border: none;
    background-color: ${({ theme }) => theme.colorBgElevated};
    background-image: url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' stroke='${({
      borderColor,
    }) =>
      encodeURIComponent(
        borderColor
      )}' stroke-width='2' stroke-dasharray='5%2c 7' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e");
    background-position: center;
  }
`;
